import { useSyncExternalStore } from 'react';
import { readStorage, writeStorage } from '@/shared/lib/storage';

/**
 * Перевод интерфейса. Ключ — сам русский текст: код читается как раньше, а английский
 * берётся из словарей src/shared/i18n/en/*.ts. Нет перевода — показываем русский.
 *
 *   t('Новое сообщение')                     → «New message»
 *   t('Был(а) в сети {when}', { when })      → подстановка
 *   tn(3, '{n} ответ|{n} ответа|{n} ответов') → «3 ответа» / «3 replies» (в словаре: '{n} reply|{n} replies')
 */
export type Lang = 'ru' | 'en';
export type Dict = Readonly<Record<string, string>>;

const KEY = 'language';

function initial(): Lang {
  return readStorage(KEY) === 'en' ? 'en' : 'ru';
}

/**
 * Английские словари (около 1,4 МБ) грузятся отдельным файлом и только тем, кто выбрал English: русскому интерфейсу они не нужны.
 * До загрузки `t` отдаёт русский текст; приложение при английском языке ждёт `i18nReady` перед первым кадром.
 */
let EN: Dict = {};
let dictionaries: Readonly<Record<string, Dict>> = {};
let loading: Promise<void> | null = null;

export function loadEnglish(): Promise<void> {
  loading ??= import('./englishBundle').then((m) => {
    dictionaries = m.default;
    EN = Object.values(m.default).reduce<Record<string, string>>((all, dict) => ({ ...all, ...dict }), {});
  });
  return loading;
}

let lang: Lang = initial();
/** Готово, когда загружен словарь выбранного языка (для русского — сразу). */
export const i18nReady: Promise<void> = lang === 'en' ? loadEnglish() : Promise.resolve();
const listeners = new Set<() => void>();

export function getLang(): Lang {
  return lang;
}

/** Сменить язык: интерфейс перерисуется целиком (App подписан на язык). */
export function setLang(next: Lang): void {
  if (next === lang) return;
  if (next === 'en' && Object.keys(EN).length === 0) {
    // Словарь ещё не загружен: язык переключится сразу после загрузки (доли секунды).
    void loadEnglish().then(() => {
      applyLang(next);
    });
    return;
  }
  applyLang(next);
}

function applyLang(next: Lang): void {
  if (next === lang) return;
  lang = next;
  writeStorage(KEY, next === 'ru' ? null : next);
  if (typeof document !== 'undefined') document.documentElement.lang = next;
  listeners.forEach((l) => {
    l();
  });
}

export function subscribeLang(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useLang(): Lang {
  return useSyncExternalStore(subscribeLang, getLang, getLang);
}

/** Код языка для Intl: даты, числа, сортировка. */
export function locale(): string {
  return lang === 'en' ? 'en-US' : 'ru-RU';
}

function fill(text: string, vars: Readonly<Record<string, string | number>> | undefined): string {
  if (vars === undefined) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = vars[name];
    return value === undefined ? whole : String(value);
  });
}

/** Есть ли перевод (для проверок и тестов). */
export function hasTranslation(ru: string): boolean {
  return Object.hasOwn(EN, ru);
}

/**
 * Одно и то же русское слово бывает разным по-английски: «Тема» канала — Topic, «Тема» оформления — Theme.
 * Тогда ключ с пометкой: t('канал::Тема') — по-русски «Тема», а перевод ищется по всему ключу.
 */
function plain(key: string): string {
  const at = key.indexOf('::');
  return at < 0 ? key : key.slice(at + 2);
}

/** Тексты сервера с подстановкой («Сервер {host} не найден…»): ключ словаря server с {имя} → шаблон. */
let serverPatterns: { re: RegExp; names: string[]; en: string }[] | null = null;

/**
 * Перевод заголовка ошибки сервера: сначала точный текст, затем шаблоны из словаря server — сервер
 * подставляет в текст адрес, порт или причину, и такой текст целиком в словаре не найти.
 */
export function tServer(ru: string): string {
  if (lang !== 'en' || Object.hasOwn(EN, ru)) return t(ru);
  serverPatterns ??= Object.entries(dictionaries['./en/server.ts'] ?? {})
    .filter(([key]) => /\{\w+\}/.test(key))
    .map(([key, en]) => {
      const names: string[] = [];
      const source = key.split(/\{(\w+)\}/).map((part, i) => {
        if (i % 2 === 1) {
          names.push(part);
          return '(.+?)';
        }
        return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      });
      return { re: new RegExp(`^${source.join('')}$`, 's'), names, en };
    });
  for (const p of serverPatterns) {
    const m = p.re.exec(ru);
    if (m !== null) return fill(p.en, Object.fromEntries(p.names.map((name, i) => [name, m[i + 1] ?? ''])));
  }
  return ru;
}

export function t(ru: string, vars?: Readonly<Record<string, string | number>>): string {
  const text = lang === 'en' ? (EN[ru] ?? plain(ru)) : plain(ru);
  return fill(text, vars);
}

function ruForm(n: number, forms: readonly string[]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  const index = mod10 === 1 && mod100 !== 11 ? 0 : mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20) ? 1 : 2;
  return forms[Math.min(index, forms.length - 1)] ?? '';
}

/** Число со словом: русские формы «один|два|пять», английские в словаре — «one|other». {n} — само число. */
export function tn(n: number, ru: string, vars?: Readonly<Record<string, string | number>>): string {
  const all = { n, ...vars };
  if (lang === 'en') {
    const en = EN[ru];
    if (en !== undefined) {
      const [one = '', other = one] = en.split('|');
      return fill(n === 1 ? one : other, all);
    }
  }
  return fill(ruForm(n, ru.split('|')), all);
}
